import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { DISPLAY_FONT, TREND_COLORS } from '../../trendTheme';
import { useTrendAdmin } from '../TrendAdminContext';
import { sectionTitle } from '../adminUi';
import { formatShortDate } from '../adminHelpers';

export default function SettingsTab() {
  const {
    canAdmin,
    busyAction,
    handleSaveSettings,
    settings,
    setSettings,
    multiValueDrafts,
    setMultiValueDrafts,
    isResetBusy,
    handleOpenResetDialog,
    resetSummary,
    handleDownloadResetSnapshot,
  } = useTrendAdmin();

  return (
    <Stack spacing={3}>
      {!canAdmin && (
        <Alert severity="info">
          Du kan läsa profil och inställningar här, men bara användare med <strong>trends:admin</strong> kan spara ändringar och använda beta-reset.
        </Alert>
      )}

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!canAdmin || busyAction === 'saveSettings'}
          onClick={() => void handleSaveSettings()}
          sx={{ bgcolor: TREND_COLORS.navy, '&:hover': { bgcolor: TREND_COLORS.navyLight } }}
        >
          {canAdmin ? 'Spara inställningar' : 'Endast admin kan spara'}
        </Button>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
            {sectionTitle('Modulinställningar', 'Aktivera/inaktivera modulen, styra körningsgränser.')}
            {settings && (
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Trendmodulen aktiv</Typography>
                  <Switch checked={settings.enabled} onChange={(e) => setSettings((p) => p ? { ...p, enabled: e.target.checked } : p)} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Scheduler aktiv</Typography>
                  <Switch checked={settings.schedulerEnabled} onChange={(e) => setSettings((p) => p ? { ...p, schedulerEnabled: e.target.checked } : p)} />
                </Stack>
                <TextField size="small" label="Schematid" value={settings.scheduleTime} onChange={(e) => setSettings((p) => p ? { ...p, scheduleTime: e.target.value } : p)} />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField fullWidth size="small" type="number" label="Max källor/körning" helperText="Antal RSS-källor per körning." value={settings.sourceLimits.maxSourcesPerRun}
                      onChange={(e) => setSettings((p) => p ? ({ ...p, sourceLimits: { ...p.sourceLimits, maxSourcesPerRun: Number(e.target.value || 0) } }) : p)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField fullWidth size="small" type="number" label="Max källposter/källa" helperText="Artiklar som hämtas per källa." value={settings.sourceLimits.maxSourceItemsPerSource}
                      onChange={(e) => setSettings((p) => p ? ({ ...p, sourceLimits: { ...p.sourceLimits, maxSourceItemsPerSource: Number(e.target.value || 0) } }) : p)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField fullWidth size="small" type="number" label="Max verifierade trendtecken" helperText="Trendtecken som dubbelkollas av AI." value={settings.sourceStrategy.maxSignalsToVerify}
                      onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, maxSignalsToVerify: Number(e.target.value || 0) } }) : p)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField fullWidth size="small" type="number" label="Max trendtecken för matchning" helperText="Trendtecken som matchas mot produkter." value={settings.sourceStrategy.maxSignalsToMatch}
                      onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, maxSignalsToMatch: Number(e.target.value || 0) } }) : p)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth size="small" type="number" label="Max ålder för aktiv trend (dagar)" helperText="Äldre trender filtreras bort från sidorna." value={settings.sourceStrategy.maxSignalAgeDays}
                      onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, maxSignalAgeDays: Number(e.target.value || 0) } }) : p)} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 40, px: 1.5, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 1 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body2">Tillåt odaterat underlag</Typography>
                        <Tooltip title="Vissa källor saknar publiceringsdatum. Om på inkluderas de ändå. Rekommenderas PÅ om du kör Discovery.">
                          <IconButton size="small" sx={{ color: 'text.secondary', p: 0.25 }}><InfoOutlinedIcon sx={{ fontSize: '1rem' }} /></IconButton>
                        </Tooltip>
                      </Stack>
                      <Switch checked={settings.sourceStrategy.allowUndatedSourceItems}
                        onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, allowUndatedSourceItems: e.target.checked } }) : p)} />
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
            {sectionTitle('Källstrategi', 'Prioritera vilka typer av trendunderlag som ska ha extra fokus.')}
            {settings && (
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Prioritera receptspår</Typography>
                  <Switch checked={settings.sourceStrategy.prioritizeRecipeSignals}
                    onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, prioritizeRecipeSignals: e.target.checked } }) : p)} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Prioritera nordiska källor</Typography>
                  <Switch checked={settings.sourceStrategy.prioritizeNordicSources}
                    onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, prioritizeNordicSources: e.target.checked } }) : p)} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Prioritera foodservice-spår</Typography>
                  <Switch checked={settings.sourceStrategy.prioritizeFoodserviceSignals}
                    onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, prioritizeFoodserviceSignals: e.target.checked } }) : p)} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2">Social receptpilot</Typography>
                  <Switch checked={settings.sourceStrategy.includeSocialRecipePilot}
                    onChange={(e) => setSettings((p) => p ? ({ ...p, sourceStrategy: { ...p.sourceStrategy, includeSocialRecipePilot: e.target.checked } }) : p)} />
                </Stack>
                <TextField multiline minRows={2} label="Prioriterade teman" helperText="Kommatecken eller radbrytningar."
                  value={multiValueDrafts.prioritizedThemes} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, prioritizedThemes: e.target.value }))} />
                <TextField multiline minRows={2} label="Sociala pilottermer" helperText="Kommatecken eller radbrytningar."
                  value={multiValueDrafts.socialPilotTerms} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, socialPilotTerms: e.target.value }))} />
                <TextField multiline minRows={2} label="Designfokus" helperText="Kommatecken eller radbrytningar. T.ex. etikettdesign, visuell identitet, hyllsynlighet."
                  value={multiValueDrafts.designFocusAreas} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, designFocusAreas: e.target.value }))} />
                <TextField multiline minRows={2} label="Kundbeteenden att bevaka" helperText="Kommatecken eller radbrytningar. T.ex. målgrupper, användningstillfällen, butiksmönster."
                  value={multiValueDrafts.consumerInsightFocus} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, consumerInsightFocus: e.target.value }))} />
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Accordion
        disableGutters
        defaultExpanded={false}
        sx={{ bgcolor: TREND_COLORS.softNavy, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3, boxShadow: 'none', '&:before': { display: 'none' }, overflow: 'hidden' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: TREND_COLORS.navy }}>
            Vad betyder inställningarna?
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Trendmodulen aktiv</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Huvudbrytare för hela trendmodulen. Om den stängs av visas inga trendsidor alls.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Scheduler aktiv</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Om denna är på körs trendpipelinen automatiskt enligt schematiden. Om den är av måste du köra manuellt via "KÖR NU".
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Schematid</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Klockslaget då den automatiska körningen startar varje dag (om scheduler är aktiv).
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Max källor/körning</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Hur många RSS-källor som processas per körning. Högre = bredare bevakning men längre körtid och mer API-kostnad.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Max källposter/källa</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Hur många artiklar som hämtas från varje källa per körning. Höjer du detta fångar du fler artiklar, men det kostar mer i AI-analys.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Max verifierade trendtecken</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Max antal trendtecken som verifieras med en extra AI-kontroll per körning. Ger bättre kvalitet men kostar extra API-anrop.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Max trendtecken för matchning</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Hur många trendinsikter som matchas mot Fontanas produkter och recept. Tyngsta steget — varje matchning gör flera AI-anrop.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Max ålder för aktiv trend (dagar)</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Trender äldre än detta antal dagar filtreras bort från alla sidor. 120 dagar = senaste ~4 månaderna.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Tillåt odaterat underlag</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Vissa källor saknar publiceringsdatum. Om på inkluderas de ändå. Rekommenderas PÅ om du kör Discovery.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: TREND_COLORS.navy, mt: 1, mb: 1 }}>
              Källstrategi
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Prioritera receptspår</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Ger extra vikt åt trender kopplade till recept och matlagning. Gör att receptrelaterade insikter hamnar högre i resultaten.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Prioritera nordiska källor</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Ger extra vikt åt skandinaviska och nordiska källor, så att lokalt relevanta trender inte drunknar bland internationella.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Prioritera foodservice-spår</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Lyfter trender inom restaurang, storkök och foodservice. Relevant om Fontana bevakar proffsmarknaden.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Social receptpilot</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Aktiverar bevakning av sociala medier (TikTok, Instagram) för virala recepttrender via de sociala pilottermerna.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Prioriterade teman</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Teman som AI:n aktivt letar efter i källorna. Styr vilka ämnesområden som får extra fokus i analysen.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Sociala pilottermer</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Söktermer som används för att hitta virala trender på sociala medier. Ju mer specifika, desto bättre träffar.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Designfokus</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Styr vilka designområden (etikett, visuell identitet, typografi, färgval) som AI:n bevakar inom förpackningsdesign.
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy }}>Kundbeteenden att bevaka</Typography>
                <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                  Definierar vilka konsumentbeteenden som ska fångas: målgrupper, köpmotiv, användningstillfällen, retailmönster.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ mt: 1, p: 2, bgcolor: TREND_COLORS.cardBg, borderRadius: 2, border: `1px solid ${TREND_COLORS.divider}` }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                Sammanfattning — vad som påverkar vad:
              </Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                    <strong>Snabbare körningar?</strong><br />Sänk "Max trendtecken för matchning" och "Max källor/körning"
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                    <strong>Bredare bevakning?</strong><br />Höj "Max källor/körning" och "Max källposter/källa"
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                    <strong>Mer historik?</strong><br />Höj "Max ålder för aktiv trend"
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.5, display: 'block' }}>
                    <strong>Bättre kvalitet per trend?</strong><br />Höj "Max verifierade trendtecken"
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
        </AccordionDetails>
      </Accordion>

      <Paper sx={{ p: 3, bgcolor: TREND_COLORS.cardBg, border: `1px solid ${TREND_COLORS.divider}`, borderRadius: 3 }}>
        {sectionTitle('Fontana-profil', 'Denna information hjälper AI:n att tolka trendunderlag utifrån Fontanas verklighet, marknad och sortiment.')}
        {settings && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Prisposition" value={settings.fontanaContext.pricePosition}
                onChange={(e) => setSettings((p) => p ? ({ ...p, fontanaContext: { ...p.fontanaContext, pricePosition: e.target.value } }) : p)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" multiline minRows={2} label="Hälsopositionering" value={settings.fontanaContext.healthPositioning}
                onChange={(e) => setSettings((p) => p ? ({ ...p, fontanaContext: { ...p.fontanaContext, healthPositioning: e.target.value } }) : p)} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="Aktiva marknader" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.activeMarkets} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, activeMarkets: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="Prioriterade kategorier" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.priorityCategories} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, priorityCategories: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="Prioriterade ingredienser" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.priorityIngredients} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, priorityIngredients: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="Receptfokus" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.recipeFocus} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, recipeFocus: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="Tillväxtmål" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.growthGoals} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, growthGoals: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="Försäljningskanaler" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.salesChannels} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, salesChannels: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="DVH-fokus" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.dvhFocus} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, dvhFocus: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth multiline minRows={2} label="Foodservice-fokus" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.foodserviceFocus} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, foodserviceFocus: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline minRows={2} label="Teman att undvika" helperText="Kommatecken eller radbrytningar."
                value={multiValueDrafts.avoidThemes} onChange={(e) => setMultiValueDrafts((p) => ({ ...p, avoidThemes: e.target.value }))} />
            </Grid>
          </Grid>
        )}
      </Paper>

      {canAdmin && (
        <Paper
          sx={{
            p: 3,
            bgcolor: '#FFF5F5',
            border: '1px solid #FEB2B2',
            borderRadius: 3,
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Box>
                <Typography sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: '0.95rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9B2C2C', mb: 0.75 }}>
                  Farlig zon: betaåterställning
                </Typography>
                <Typography variant="body2" sx={{ color: TREND_COLORS.textPrimary, lineHeight: 1.7, maxWidth: 760 }}>
                  Hård återställning rensar all genererad trenddata, inklusive dedupliceringsunderlag, berättelser, möjligheter, körhistorik och artefakter.
                  Källor, discovery-frågor och trendinställningar lämnas orörda så att nästa körning blir en helt ny baslinje.
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={isResetBusy}
                  onClick={() => void handleOpenResetDialog()}
                >
                  {isResetBusy ? 'Laddar förhandsvisning...' : 'Öppna betaåterställning'}
                </Button>
              </Stack>
            </Stack>

            <Alert severity="warning">
              Snapshot ska alltid skapas före återställning. Om du väljer att starta en ny trendkörning direkt efteråt blir all data som byggs upp efter återställningen lätt att identifiera som ny.
            </Alert>

            {resetSummary && (
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#FFFFFF', border: '1px solid #FED7D7' }}>
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#9B2C2C' }}>
                        Senaste hårda återställning
                      </Typography>
                      <Typography variant="caption" sx={{ color: TREND_COLORS.textSecondary }}>
                        {formatShortDate(resetSummary.resetAt)} - Snapshot: {resetSummary.snapshotFileName}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => void handleDownloadResetSnapshot(resetSummary.snapshotId, resetSummary.snapshotFileName)}
                    >
                      Ladda ner snapshot igen
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip color="error" variant="outlined" label={`${resetSummary.totalDocumentsDeleted} dokument rensade`} />
                    <Chip color="warning" variant="outlined" label={`${resetSummary.totalFilesDeleted} artefaktfiler rensade`} />
                    <Chip
                      color={resetSummary.restartedRun ? 'success' : 'default'}
                      label={resetSummary.restartedRun ? 'Ny trendkörning startades' : 'Ingen ny trendkörning startades'}
                    />
                  </Stack>

                  {resetSummary.restartError && (
                    <Alert severity="warning">{resetSummary.restartError}</Alert>
                  )}

                  <Grid container spacing={1.5}>
                    {resetSummary.clearedCollections.map((entry) => (
                      <Grid key={entry.key} size={{ xs: 12, sm: 6, md: 3 }}>
                        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#FFFAF0', border: '1px solid #FBD38D' }}>
                          <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, display: 'block' }}>
                            {entry.label}
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: '#9B2C2C' }}>
                            {entry.count}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
